import { tz } from "@date-fns/tz";
import { useDRPContext } from "@inputs/date/DateRangePicker/context";
import { rangeDateToString } from "@inputs/date/DateRangePicker/logics/date";
import type { IDRPHeaderForm } from "@inputs/date/DateRangePicker/types";
import { endOfDay, startOfDay } from "date-fns";
import { useCallback, useEffect } from "react";
import { type FieldErrorsImpl, type Resolver, useForm } from "react-hook-form";

const dateRegex = /^(\d{2})\/(\d{2})\/(\d{4})$/;
const dateFormatOptions: Intl.DateTimeFormatOptions = { day: "2-digit", month: "2-digit", year: "numeric" };

const parseDate = (value: string) => {
  const match = value.match(dateRegex);
  if (match) {
    const day = Number(match[1]) || 1;
    const month = (Number(match[2]) || 1) - 1;
    const year = Number(match[3]) || 0;
    return { day, month, year };
  }
  return null;
};

const resolver: Resolver<IDRPHeaderForm> = (values) => {
  const start = parseDate(values.startDate);
  const end = parseDate(values.endDate);

  const errors: Partial<FieldErrorsImpl<IDRPHeaderForm>> = {};
  //Month
  if (start && (start.month < 0 || start.month > 11)) {
    errors.startDate = { type: "validate", message: "La date est invalide" };
  }
  if (end && (end.month < 0 || end.month > 11)) {
    errors.endDate = { type: "validate", message: "La date est invalide" };
  }
  //Day
  if (start && (start.day < 0 || start.day > new Date(start.year, start.month + 1, 0).getDate())) {
    errors.startDate = { type: "validate", message: "La date est invalide" };
  }
  if (end && (end.day < 0 || end.day > new Date(end.year, end.month + 1, 0).getDate())) {
    errors.endDate = { type: "validate", message: "La date est invalide" };
  }

  if (errors.startDate ?? errors.endDate) {
    return { values: {}, errors };
  }
  if (start && end && (start.year > end.year || (start.year === end.year && start.month > end.month))) {
    return {
      values: {},
      errors: {
        startDate: { type: "validate", message: "La date de début ne peut pas être supérieure à la date de fin" },
      },
    };
  }

  if (errors.startDate ?? errors.endDate) {
    return { values: {}, errors };
  }
  return { values, errors: {} };
};

export const useDRDPHeader = () => {
  const { range, handleRangeChange, error, required, timeZone } = useDRPContext();
  const { reset, control, handleSubmit } = useForm<IDRPHeaderForm>({
    defaultValues: rangeDateToString(range, dateFormatOptions, timeZone),
    resolver,
  });

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    reset(rangeDateToString(range, dateFormatOptions, timeZone));
  }, [range]);

  const submit = useCallback(
    (values: IDRPHeaderForm) => {
      const start = parseDate(values.startDate);
      const end = parseDate(values.endDate);
      const tzCtx = { in: tz(timeZone) };

      handleRangeChange({
        startDate: start ? startOfDay(new Date(start.year, start.month, start.day), tzCtx) : null,
        endDate: end ? endOfDay(new Date(end.year, end.month, end.day), tzCtx) : null,
      });
    },
    [handleRangeChange, timeZone]
  );

  return { control, submit: handleSubmit(submit), error, required };
};
